import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Boxes, FolderKanban, Users } from "lucide-react";

import { LandingShell } from "../components/LandingShell";
import { useOverview } from "../hooks/useClients";

type LandingCard = {
  id: string;
  name: string;
  datasetCount: number;
  datasets: Array<{
    id: string;
    name: string;
  }>;
  footer: string;
};

export function LandingPage() {
  // Keep the landing page on a lightweight catalog-only path.
  const { data: overview, isLoading, error } = useOverview();
  const clients = overview?.clients ?? [];

  const [activeClient, setActiveClient] = useState<string>("all");

  const stats = useMemo(() => {
    const totalClients = overview?.totalClients ?? 0;
    const totalDatasets = overview?.totalDatasets ?? 0;
    const totalShipments = overview?.totalShipments ?? null;
    const totalEvents = null;
    return { totalClients, totalDatasets, totalShipments, totalEvents };
  }, [overview]);

  const cards = useMemo<LandingCard[]>(() => {
    return clients
      .map((client) => ({
        id: client.id,
        name: client.name,
        datasetCount: client.datasetCount,
        datasets: client.datasets ?? [],
        footer: "Ouvrir le client pour charger les datasets détaillés.",
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [clients]);

  useEffect(() => {
    if (activeClient !== "all" && !cards.some((client) => client.id === activeClient)) {
      setActiveClient("all");
    }
  }, [activeClient, cards]);

  const visibleClients =
    activeClient === "all" ? cards : cards.filter((client) => client.id === activeClient);

  return (
    <LandingShell
      active="overview"
      breadcrumb="Accueil"
      searchPlaceholder="Rechercher clients..."
    >
      <section className="landing-pro__stats">
        <div className="landing-pro__stat-card">
          <div className="landing-pro__stat-header">
            <div>
              <p className="landing-pro__stat-label">Total Clients</p>
              <p className="landing-pro__stat-value">{stats.totalClients}</p>
            </div>
            <div className="landing-pro__stat-icon" aria-hidden="true">
              <Users size={20} />
            </div>
          </div>
        </div>
        <div className="landing-pro__stat-card">
          <div className="landing-pro__stat-header">
            <div>
              <p className="landing-pro__stat-label">Datasets Actifs</p>
              <p className="landing-pro__stat-value">{stats.totalDatasets}</p>
            </div>
            <div className="landing-pro__stat-icon" aria-hidden="true">
              <FolderKanban size={20} />
            </div>
          </div>
        </div>
        <div className="landing-pro__stat-card">
          <div className="landing-pro__stat-header">
            <div>
              <p className="landing-pro__stat-label">Expéditions</p>
              <p className="landing-pro__stat-value">{stats.totalShipments ?? "—"}</p>
            </div>
            <div className="landing-pro__stat-icon" aria-hidden="true">
              <Boxes size={20} />
            </div>
          </div>
        </div>
      </section>

      {isLoading ? <p className="dashboard-hint">Chargement des clients...</p> : null}
      {error ? (
        <p role="alert" className="dashboard-error">
          Erreur : {error.message}
        </p>
      ) : null}

      <div className="landing-pro__tabs">
        <button
          className={`landing-pro__tab${activeClient === "all" ? " is-active" : ""}`}
          type="button"
          onClick={() => setActiveClient("all")}
        >
          Tous les clients
        </button>
        {cards.map((client) => (
          <button
            key={client.id}
            className={`landing-pro__tab${activeClient === client.id ? " is-active" : ""}`}
            type="button"
            onClick={() => setActiveClient(client.id)}
          >
            {client.name.toUpperCase()}
          </button>
        ))}
      </div>

      <section className="landing-pro__cards">
        {visibleClients.map((client) => (
          <Link key={client.id} to={`/clients/${encodeURIComponent(client.id)}`} className="landing-pro__card">
            <header className="landing-pro__card-header">
              <h3>{client.name}</h3>
              <p>{client.datasetCount} dataset{client.datasetCount > 1 ? "s" : ""}</p>
            </header>
            <div className="landing-pro__card-datasets">
              {client.datasets.map((dataset) => (
                <div key={dataset.id} className="landing-pro__dataset-tile">
                  <strong>{dataset.name}</strong>
                  <span>{dataset.id}</span>
                </div>
              ))}
            </div>
            <footer className="landing-pro__card-footer">
              <span>{client.footer}</span>
            </footer>
          </Link>
        ))}
      </section>

      {!isLoading && !error && visibleClients.length === 0 ? (
        <p className="dashboard-hint">Aucun client trouvé.</p>
      ) : null}
    </LandingShell>
  );
}
